import dayjs from "dayjs";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import FlexPassApi from "../../../api/flexpassApi";
import SpecialistApi from "../../../api/specialistsApi";
import { ReactComponent as DeleteIcon } from "../../../assets/Companies/delete.svg";
import Input from "../../../components/Input/Input";
import MuiDatePicker from "../../../components/MuiDatePicker/MuiDatePicker";
import MuiSwitch from "../../../components/MuiSwitch/MuiSwitch";
import MultipleSelect from "../../../components/MultipleSelect/MultipleSelect";
import StandartButton from "../../../components/StandartButton/StandartButton";
import { ISO639Lang } from "../../../constans/languagesList";
import { siteLangs } from "../../../constans/siteLangs";
import {
  FlexPassSubscription,
  NewFlexPass,
  SubscriptionType,
} from "../../../types/FlexPassTypes";
import { getTranslation } from "../../../utils/getTranslation";
import Notify from "../../../utils/toaster";
import {
  flexPassTypeStrings,
  isFlexPassValid,
  parseNumberInRange,
} from "../flexPassUtils";
import s from "./CreateFlexPass.module.css";

type DropDownMenuData = { lable: string; value: string };

type SelectFieldsData = {
  languages: DropDownMenuData[];
  mainSpecializations: DropDownMenuData[];
  levels: DropDownMenuData[];
  types: DropDownMenuData[];
};

const initialNewFlexPass = {
  title: [{ text: "", language: "en" }],
  description: [{ text: "", language: "en" }],
  language: "",
  mainSpecialization: "",
  level: "",
  startDate: new Date(),
  endDate: new Date(),
  cost: 0,
  duration: 0,
  type: "" as SubscriptionType,
  advantages: [],
  isUniversal: false,
};

type CreateFlexPassProps = {
  setFlexPasses: Dispatch<SetStateAction<FlexPassSubscription[]>>;
  setCreateFlexPassModal: Dispatch<SetStateAction<boolean>>;
  setSelectedFlexPass: Dispatch<
    SetStateAction<FlexPassSubscription | undefined>
  >;
  isUpdatePass?: boolean;
  flexPassPropsData?: FlexPassSubscription;
};

const CreateFlexPass = ({
  setFlexPasses,
  setCreateFlexPassModal,
  setSelectedFlexPass,
  isUpdatePass,
  flexPassPropsData,
}: CreateFlexPassProps) => {
  const token = localStorage.getItem("token");
  const [flexPassData, setFlexPassData] =
    useState<NewFlexPass>(initialNewFlexPass);
  const [selectFieldsData, setSelectFieldsData] = useState<SelectFieldsData>({
    languages: [],
    mainSpecializations: [],
    levels: [],
    types: [],
  });
  const [loaders, setloaders] = useState({ createLoader: false });
  const [intermediateData, setIntermediateData] = useState({
    firstSessionPayoutInput:
      flexPassData.firstSessionPayout !== undefined
        ? flexPassData.firstSessionPayout.toString()
        : "",
    cost: flexPassData.cost !== undefined ? flexPassData.cost.toString() : "",
    repeatSessionPayout:
      flexPassData.repeatSessionPayout !== undefined
        ? flexPassData.repeatSessionPayout.toString()
        : "",
    baseCost:
      flexPassData.baseCost !== undefined && flexPassData.baseCost !== null
        ? flexPassData.baseCost.toString()
        : "",
  });

  const getMainSpecializations = async () => {
    if (!token) return;
    const response = await SpecialistApi.getMainSpecializations(token);
    if (response.status && response.mainSpecializations) {
      setSelectFieldsData((prev) => ({
        ...prev,
        mainSpecializations: response.mainSpecializations!.map((item) => ({
          lable: getTranslation(item.labels, "en"),
          value: item._id,
        })),
      }));
    }
  };

  useEffect(() => {
    getMainSpecializations();
    setSelectFieldsData((prev) => ({
      ...prev,
      languages: siteLangs.map((item) => ({
        lable: ISO639Lang[item.value],
        value: item.value,
      })),
      types: [
        {
          value: SubscriptionType.DISCOUNT,
          lable: flexPassTypeStrings[SubscriptionType.DISCOUNT],
        },
        {
          value: SubscriptionType.SESSION_VOLUME,
          lable: flexPassTypeStrings[SubscriptionType.SESSION_VOLUME],
        },
      ],
    }));
  }, []);

  const getSpecialiationLevels = async () => {
    if (!flexPassData.mainSpecialization.length || !token) return;
    const response = await SpecialistApi.getAllSpecialistLevels({
      mainSpecId: flexPassData.mainSpecialization,
    });
    if (response.status && response.levels) {
      const levelsData = response.levels.map((item) => ({
        value: item._id,
        lable: getTranslation(item.title, "en"),
      }));
      setSelectFieldsData((prev) => ({ ...prev, levels: levelsData }));
    }
  };

  useEffect(() => {
    getSpecialiationLevels();
  }, [flexPassData.mainSpecialization]);

  useEffect(() => {
    if (!flexPassPropsData) return;

    setIntermediateData({
      firstSessionPayoutInput:
        flexPassPropsData.firstSessionPayout !== undefined
          ? (flexPassPropsData.firstSessionPayout / 100).toString()
          : "",
      cost:
        flexPassPropsData.cost !== undefined
          ? (flexPassPropsData.cost / 100).toString()
          : "",
      baseCost:
        flexPassPropsData.baseCost !== undefined
          ? (flexPassPropsData.baseCost / 100).toString()
          : "",
      repeatSessionPayout:
        flexPassPropsData.repeatSessionPayout !== undefined
          ? (flexPassPropsData.repeatSessionPayout / 100).toString()
          : "",
    });
    setFlexPassData({
      cost: flexPassPropsData.cost / 100,
      duration: flexPassPropsData.duration,
      endDate: new Date(flexPassPropsData.endDate),
      language: flexPassPropsData.language,
      level: flexPassPropsData.level._id,
      mainSpecialization: flexPassPropsData.mainSpecialization._id,
      startDate: new Date(flexPassPropsData.startDate),
      title: flexPassPropsData.title,
      type: flexPassPropsData.type,
      amountOfSessions: flexPassPropsData.amountOfSessions,
      description: flexPassPropsData.description,
      firstSessionPayout: flexPassPropsData.firstSessionPayout
        ? flexPassPropsData.firstSessionPayout / 100
        : undefined,
      repeatSessionPayout: flexPassPropsData.repeatSessionPayout
        ? flexPassPropsData.repeatSessionPayout / 100
        : undefined,
      sessionDiscount: flexPassPropsData.sessionDiscount,
      talziCommissionPercent: flexPassPropsData.talziCommissionPercent,
      isUniversal: flexPassPropsData.isUniversal,
      advantages: flexPassPropsData.advantages,
    });
  }, [flexPassPropsData]);

  const createFlexPass = async () => {
    if (!token) return;
    setloaders((prev) => ({ ...prev, createLoader: true }));
    const response = await FlexPassApi.createFlexPassSubscription(token, {
      ...flexPassData,
      cost: flexPassData.cost * 100,
      baseCost: flexPassData.baseCost ? flexPassData.baseCost * 100 : null,
      firstSessionPayout:
        flexPassData.firstSessionPayout &&
        flexPassData.firstSessionPayout * 100,
      repeatSessionPayout:
        flexPassData.repeatSessionPayout &&
        flexPassData.repeatSessionPayout * 100,
    });
    setloaders((prev) => ({ ...prev, createLoader: false }));
    if (response.status && response.subscription) {
      setFlexPasses((prev) => [response.subscription!, ...prev]);
      setCreateFlexPassModal(false);
    }
    if (!response.status && response.message) {
      Notify(response.message);
    }
  };

  const updateFlexPass = async () => {
    if (!token || !flexPassPropsData) return;
    setloaders((prev) => ({ ...prev, createLoader: true }));
    const response = await FlexPassApi.updateFlexPassSubscription(token, {
      ...flexPassData,
      _id: flexPassPropsData._id,
      cost: flexPassData.cost * 100,
      baseCost: flexPassData.baseCost ? flexPassData.baseCost * 100 : null,
      firstSessionPayout:
        flexPassData.firstSessionPayout &&
        flexPassData.firstSessionPayout * 100,
      repeatSessionPayout:
        flexPassData.repeatSessionPayout &&
        flexPassData.repeatSessionPayout * 100,
    });
    setloaders((prev) => ({ ...prev, createLoader: false }));
    if (response.status && response.subscription) {
      setFlexPasses((prev) => {
        return prev.map((item) => {
          if (item._id === flexPassPropsData._id) {
            return response.subscription!;
          }
          return item;
        });
      });
      setSelectedFlexPass(response.subscription);
      setCreateFlexPassModal(false);
    }
    if (!response.status && response.message) {
      Notify(response.message);
    }
  };

  return (
    <div className={s.container}>
      <div className={s.titleBlock}>
        <h2 className={s.headTitle}>
          {isUpdatePass ? "Update FlexPass" : "Create FlexPass"}
        </h2>
        <Input
          inputValue={flexPassData.title[0].text}
          onChangeInput={(value) =>
            setFlexPassData((prev) => ({
              ...prev,
              title: prev.title.map((item) => ({ ...item, text: value })),
            }))
          }
          isVisible
          required
          lable="FlexPass title"
        />
      </div>
      <div className={s.descriptionBlock}>
        <span className={s.inputTitle}>
          Enter the description of the FlexPass
        </span>
        <textarea
          name=""
          id=""
          cols={30}
          rows={7}
          value={flexPassData.description && flexPassData.description[0].text}
          className={s.textArea}
          onChange={(event) =>
            setFlexPassData((prev) => ({
              ...prev,
              description: [
                { language: prev.language, text: event.target.value },
              ],
            }))
          }
        ></textarea>
      </div>
      <div>
        <MuiSwitch
          checked={flexPassData.isUniversal}
          setChecked={(value) =>
            setFlexPassData((prev) => ({ ...prev, isUniversal: value }))
          }
          lable="Please enable this feature if you want this FlexPass to be displayed on an external website."
          lablePlacement="start"
        />
      </div>
      <div className={s.advantages}>
        <div className={s.advantageInputBlock}>
          <div className={s.inputTitle}>
            Advantages ({(flexPassData.advantages ?? []).length} - 7)
          </div>

          {(!flexPassData.advantages || flexPassData.advantages.length < 7) && (
            <StandartButton
              action={() => {
                setFlexPassData((prev) => ({
                  ...prev,
                  advantages: [...(prev.advantages ?? []), ""],
                }));
              }}
              buttonTitle="+ Add"
              width="100px"
              height="36px"
            />
          )}
        </div>
        <div>Use **two asterisks** for bold text</div>
        {flexPassData.advantages &&
          flexPassData.advantages.map((item, index) => (
            <div key={index} className={s.advantageInputBlock}>
              <Input
                inputValue={item}
                onChangeInput={(value) =>
                  setFlexPassData((prev) => ({
                    ...prev,
                    advantages: prev.advantages
                      ? prev.advantages.map((prevString, i) => {
                          if (index === i) {
                            return value;
                          }
                          return prevString;
                        })
                      : prev.advantages,
                  }))
                }
              />
              <div
                role="button"
                onClick={() => {
                  setFlexPassData((prev) => ({
                    ...prev,
                    advantages: prev.advantages
                      ? prev.advantages.filter((_, i) => i !== index)
                      : prev.advantages,
                  }));
                }}
                className={s.deleteBtn}
              >
                <DeleteIcon className={s.deleteIcon} />
              </div>
            </div>
          ))}
      </div>
      <div className={s.inputsRow}>
        <Input
          inputValue={intermediateData.cost}
          onChangeInput={(value: string) => {
            if (parseNumberInRange(value, 0, 999999)) {
              setIntermediateData((prev) => ({
                ...prev,
                cost: value,
              }));
              if (!isNaN(Number(value)) && value !== "." && value !== "") {
                setFlexPassData((prev) => ({
                  ...prev,
                  cost: Number(value),
                }));
              }
            }
          }}
          lable="Cost($)"
          width="20%"
        />
        <Input
          inputValue={intermediateData.baseCost}
          onChangeInput={(value: string) => {
            if (parseNumberInRange(value, 0, 999999)) {
              setIntermediateData((prev) => ({
                ...prev,
                baseCost: value,
              }));
              if (!isNaN(Number(value)) && value !== "." && value !== "") {
                setFlexPassData((prev) => ({
                  ...prev,
                  baseCost: Number(value),
                }));
              }
            }
          }}
          lable="Base Cost($)"
          width="20%"
        />
        <Input
          inputValue={
            flexPassData.duration !== undefined
              ? flexPassData.duration.toString()
              : ""
          }
          onChangeInput={(value: string) => {
            if (parseNumberInRange(value, 0, 999999)) {
              setFlexPassData((prev) => ({
                ...prev,
                duration: Number(value),
              }));
            }
          }}
          lable="Duration (days)"
          width="20%"
        />
        <div style={{ width: "25%" }}>
          <MuiDatePicker
            setValue={(value) => {
              setFlexPassData((prev) => ({
                ...prev,
                startDate: value ? value.toDate() : new Date(),
              }));
            }}
            value={dayjs(flexPassData.startDate)}
            label="Start date"
          />
        </div>
        <div style={{ width: "20%" }}>
          <MuiDatePicker
            setValue={(value) => {
              setFlexPassData((prev) => ({
                ...prev,
                endDate: value ? value.toDate() : new Date(),
              }));
            }}
            value={dayjs(flexPassData.endDate)}
            label="End date"
          />
        </div>
      </div>
      <div className={s.inputsRow}>
        <MultipleSelect
          data={selectFieldsData.mainSpecializations}
          multiplie={false}
          setValue={(value) =>
            setFlexPassData((prev) => ({
              ...prev,
              mainSpecialization: value as string,
            }))
          }
          value={flexPassData.mainSpecialization}
          width="50%"
          lable="Specialization"
        />
        <MultipleSelect
          data={selectFieldsData.types}
          multiplie={false}
          setValue={(value) =>
            setFlexPassData((prev) => ({
              ...prev,
              type: value as SubscriptionType,
            }))
          }
          value={flexPassData.type}
          width="50%"
          lable="FlexPass Type"
        />
      </div>
      <div className={s.inputsRow}>
        <MultipleSelect
          data={selectFieldsData.languages}
          multiplie={false}
          setValue={(value) =>
            setFlexPassData((prev) => ({
              ...prev,
              language: value as string,
              description: [
                { ...prev.description![0], language: value as string },
              ],
              title: [{ ...prev.title![0], language: value as string }],
            }))
          }
          value={flexPassData.language}
          width="50%"
          lable="Language"
        />
        <MultipleSelect
          data={selectFieldsData.levels}
          multiplie={false}
          setValue={(value) =>
            setFlexPassData((prev) => ({
              ...prev,
              level: value as string,
            }))
          }
          value={flexPassData.level}
          width="50%"
          lable="Expert Level"
          disabled={selectFieldsData.levels.length ? false : true}
        />
      </div>
      {flexPassData.type === SubscriptionType.DISCOUNT && (
        <div className={s.inputsRow}>
          <Input
            inputValue={
              flexPassData.sessionDiscount !== undefined
                ? flexPassData.sessionDiscount.toString()
                : ""
            }
            onChangeInput={(value: string) => {
              if (parseNumberInRange(value, 0, 100)) {
                setFlexPassData((prev) => ({
                  ...prev,
                  sessionDiscount: Number(value),
                }));
              }
            }}
            lable="Session Discount(%)"
            width="50%"
          />
          <Input
            inputValue={
              flexPassData.talziCommissionPercent !== undefined
                ? flexPassData.talziCommissionPercent.toString()
                : ""
            }
            onChangeInput={(value: string) => {
              if (parseNumberInRange(value, 0, 100)) {
                setFlexPassData((prev) => ({
                  ...prev,
                  talziCommissionPercent: Number(value),
                }));
              }
            }}
            lable="Talzi Commission(%)"
            width="50%"
          />
        </div>
      )}
      {flexPassData.type === SubscriptionType.SESSION_VOLUME && (
        <div className={s.inputsRow}>
          <Input
            inputValue={
              flexPassData.amountOfSessions !== undefined
                ? flexPassData.amountOfSessions.toString()
                : ""
            }
            onChangeInput={(value: string) => {
              if (parseNumberInRange(value, 0, 100, true)) {
                setFlexPassData((prev) => ({
                  ...prev,
                  amountOfSessions: Number(value),
                }));
              }
            }}
            lable="Available sessions for the client"
            width="33%"
          />
          <Input
            inputValue={intermediateData.firstSessionPayoutInput}
            onChangeInput={(value: string) => {
              if (parseNumberInRange(value, 0, 99999)) {
                setIntermediateData((prev) => ({
                  ...prev,
                  firstSessionPayoutInput: value,
                }));

                if (!isNaN(Number(value)) && value !== "." && value !== "") {
                  setFlexPassData((prev) => ({
                    ...prev,
                    firstSessionPayout: Number(value),
                  }));
                }
              }
            }}
            lable="Payment to the expert for the first session($)"
            width="33%"
            type="text"
          />
          <Input
            inputValue={intermediateData.repeatSessionPayout}
            onChangeInput={(value: string) => {
              if (parseNumberInRange(value, 0, 99999)) {
                setIntermediateData((prev) => ({
                  ...prev,
                  repeatSessionPayout: value,
                }));
                if (!isNaN(Number(value)) && value !== "." && value !== "") {
                  setFlexPassData((prev) => ({
                    ...prev,
                    repeatSessionPayout: Number(value),
                  }));
                }
              }
            }}
            lable="Paying the expert for a repeat session($)"
            width="33%"
          />
        </div>
      )}
      <div className={s.actionBlock}>
        <StandartButton
          action={isUpdatePass ? updateFlexPass : createFlexPass}
          buttonTitle={"Save"}
          width="200px"
          disabled={loaders.createLoader || !isFlexPassValid(flexPassData)}
        />
      </div>
    </div>
  );
};

export default CreateFlexPass;
